// ST-3b — Tell someone: a tag or a task sent to the people who need it, by the
// channels those people actually have. The send is written on the thing itself.
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import DockPanel from '@/components/shell/DockPanel';
import '@/styles/tell-someone.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNELS = [['in_app', 'In app'], ['email', 'Email'], ['sms', 'Text']];
const WORDS = { in_app: 'in app', email: 'email', sms: 'text' };

const nameOf = (row) => (row.full_name
  || [row.first_name, row.last_name].filter(Boolean).join(' ')
  || row.name || row.company_name || row.email || 'Someone');

const phoneOf = (row) => row.phone_mobile || row.mobile || row.phone || row.phone_work || '';

const said = (names) => (names.length > 1
  ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
  : names[0] || 'nobody');

export const TellSomeoneSheet = ({ about, onClose, onSent }) => {
  const [caps, setCaps] = useState({ in_app: true, email: true, sms: false });
  const [people, setPeople] = useState([]);
  const [chosen, setChosen] = useState([]);
  const [channels, setChannels] = useState(['in_app']);
  const [form, setForm] = useState({ subject: '', message: '' });
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');
  const [problem, setProblem] = useState('');

  useEffect(() => {
    let alive = true;
    Promise.all([
      axios.get(`${API}/messaging/capabilities`).catch(() => ({ data: {} })),
      axios.get(`${API}/users`).catch(() => ({ data: { users: [] } })),
      axios.get(`${API}/contacts/list`).catch(() => ({ data: [] })),
      axios.get(`${API}/messaging/tell/draft`,
        { params: { about_type: about.type, about_id: about.id } })
        .catch(() => ({ data: {} })),
    ]).then(([capsRes, usersRes, contactsRes, draftRes]) => {
      if (!alive) return;
      setCaps({ in_app: true, email: true, sms: false, ...(capsRes.data || {}) });
      const team = (usersRes.data?.users || usersRes.data || []).map((row) => ({
        kind: 'user', id: row.id, name: nameOf(row),
        email: row.email || '', phone: phoneOf(row),
      }));
      const list = contactsRes.data?.contacts || contactsRes.data || [];
      const book = (Array.isArray(list) ? list : []).map((row) => ({
        kind: 'contact', id: row.id, name: nameOf(row),
        email: row.email || '', phone: phoneOf(row),
      }));
      setPeople([...team, ...book].filter((row) => row.id));
      setForm({ subject: draftRes.data?.subject || '',
        message: draftRes.data?.message || '' });
    });
    return () => { alive = false; };
  }, [about.type, about.id]);

  const ways = useMemo(() => CHANNELS.filter(([key]) => key !== 'sms' || caps.sms),
    [caps.sms]);

  const canTake = useCallback((person) => {
    const has = [];
    if (person.kind === 'user') has.push('in_app');
    if (person.email) has.push('email');
    if (person.phone && caps.sms) has.push('sms');
    return has;
  }, [caps.sms]);

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const rows = needle
      ? people.filter((person) => person.name.toLowerCase().includes(needle)
        || (person.email || '').toLowerCase().includes(needle))
      : people;
    return rows.slice(0, 60);
  }, [people, search]);

  const toggle = (id) => setChosen((held) => (held.includes(id)
    ? held.filter((one) => one !== id) : [...held, id]));

  const send = async () => {
    setProblem('');
    if (!chosen.length) { setProblem('Pick who to tell'); return; }
    if (!channels.length) { setProblem('Pick at least one way to tell them'); return; }
    setBusy(true);
    try {
      const answer = await axios.post(`${API}/messaging/tell`, {
        about,
        recipients: chosen.map((id) => {
          const person = people.find((one) => one.id === id);
          return { kind: person.kind, id };
        }),
        channels,
        subject: form.subject.trim(),
        message: form.message.trim(),
      });
      const sent = answer.data?.sent || [];
      const used = [...new Set(sent.flatMap((row) => row.channels))]
        .map((key) => WORDS[key]);
      const when = new Date().toLocaleTimeString([],
        { hour: 'numeric', minute: '2-digit' }).toLowerCase();
      setResult(sent.length
        ? `Told ${said(sent.map((row) => row.name))} by ${used.join(' · ')} · ${when}`
        : 'Nothing went out');
      onSent?.(answer.data);
      setTimeout(() => onClose?.(), 1800);
    } catch (e) {
      setProblem(e?.response?.data?.detail || 'That did not send');
    } finally { setBusy(false); }
  };

  return (
    <DockPanel onClose={onClose} width="min(440px, 100vw)">
      <aside className="ktl" data-testid="tell-sheet">
        <div className="kp-head">
          <div>
            <div className="ktl-k">Tell someone</div>
            <div className="t">{form.subject || 'Send it on'}</div>
          </div>
          <button type="button" className="ktl-btn" onClick={onClose}
            data-testid="tell-close">Close</button>
        </div>

        <div className="ktl-f">
          <span className="ktl-k">People</span>
          <input className="ktl-in ktl-search" value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="search the team and contacts" data-testid="tell-search" />
          <div className="ktl-people" data-testid="tell-people">
            {shown.map((person) => {
              const has = canTake(person);
              return (
                <button type="button" key={`${person.kind}-${person.id}`}
                  className={`ktl-person${chosen.includes(person.id) ? ' on' : ''}`}
                  onClick={() => toggle(person.id)}
                  data-testid={`tell-person-${person.id}`}>
                  <span className="box" />
                  <span className="nm">{person.name}</span>
                  <span className="ch">
                    {has.length ? has.map((key) => WORDS[key]).join(' · ') : 'no way to reach'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="ktl-f">
          <span className="ktl-k">How</span>
          <div className="ktl-tiles">
            {ways.map(([key, label]) => (
              <button type="button" key={key}
                className={`ktl-tile${channels.includes(key) ? ' on' : ''}`}
                onClick={() => setChannels((held) => (held.includes(key)
                  ? held.filter((one) => one !== key) : [...held, key]))}
                data-testid={`tell-channel-${key}`}>{label}</button>
            ))}
          </div>
        </div>

        <label className="ktl-f">
          <span className="ktl-k">Subject</span>
          <input className="ktl-in" value={form.subject} maxLength={120}
            onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
            data-testid="tell-subject" />
        </label>

        <label className="ktl-f">
          <span className="ktl-k">Message</span>
          <textarea className="ktl-in ktl-msg" value={form.message} maxLength={1000}
            onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            data-testid="tell-message" />
        </label>

        {problem ? <p className="ktl-err" data-testid="tell-error">{problem}</p> : null}
        {result ? <p className="ktl-said" data-testid="tell-result">{result}</p> : null}

        <div className="ktl-acts">
          <button type="button" className="ktl-btn ktl-btn--brand" disabled={busy}
            onClick={send} data-testid="tell-send">
            {busy ? 'Sending…' : 'Send'}
          </button>
          <button type="button" className="ktl-btn" onClick={onClose}
            data-testid="tell-later">Not now</button>
        </div>
      </aside>
    </DockPanel>
  );
};

export default TellSomeoneSheet;
